import { useState } from "react";
import BemIt, { addClass } from "@gtechdoodler/bem-it";
import Typography from "../../typography/Typography/Typography";
import useViewport from "../../../hooks/useViewport";
import { BreakpointOptions } from "../../../types/theming";
import { LazyImageBreakpointSrc, LazyImageProps } from "./LazyImage.types";
import { StyledBox } from "./LazyImage.styles";
import { ViewportResult } from "../../../hooks/useViewport/useViewport.types";
import FallbackImageDarkBackground from "./svgs/fallback_image_dark_background.svg";
import FallbackImageLightBackground from "./svgs/fallback_image_light_background.svg";

/**
 * Call to find an override that matches the currentSize/breakpoint of the viewport result object.
 * @param overrides An array of overriding images for breakpoints
 * @param vp A viewport result object
 * @returns An overriding image src url if an override is found in the array, otherwise undefined.
 */
const resolveSrc = (
	overrides: LazyImageBreakpointSrc[] | undefined,
	vp: ViewportResult
): string | undefined => {
	if (overrides) {
		for (var i = 0; i < overrides.length; i++) {
			const or = overrides[i];
			if (typeof or.breakpoint === "string" && vp.is[or.breakpoint]) {
				return or.src;
			} else if (Array.isArray(or.breakpoint)) {
				if (
					or.breakpoint.find(
						(breakpoint: BreakpointOptions) => breakpoint === vp.currentSize
					)
				) {
					return or.src;
				}
			}
		}
	}
	return undefined;
};

const LazyImage: React.FC<LazyImageProps> = ({
	alt,
	backgroundColor,
	className,
	lazy,
	src,
	srcOverrides,
	...props
}) => {
	const block = new BemIt("LazyImage");
	const vp = useViewport();
	const [isLoaded, setIsLoaded] = useState(false);
	const [isError, setIsError] = useState(false);
	const darkBackground =
		backgroundColor === "bg_dark" || backgroundColor === "bg_dark_variant";
	src =
		resolveSrc(srcOverrides, vp) !== undefined
			? resolveSrc(srcOverrides, vp)
			: src;

	const handleOnLoad = () => {
		setIsLoaded(true);
		setIsError(false);
	};

	const handleOnError = () => {
		setIsError(true);
	};
	if (!lazy) {
		const image = new Image();
		image.src = src || "";
		image.onload = handleOnLoad;
		image.onerror = handleOnError;
	}

	return (
		<StyledBox
			className={addClass(className).after(
				block.mod({
					loaded: isLoaded,
				})
			)}
			display="flex"
			alignItems="center"
			justifyContent="center"
			backgroundColor={backgroundColor}
			height={props.height}
			width={props.width}
		>
			{isLoaded && !isError ? (
				<img alt={alt} src={src} {...props} />
			) : isError ? (
				<img
					alt="No image available"
					src={
						darkBackground
							? FallbackImageDarkBackground
							: FallbackImageLightBackground
					}
					{...props}
				/>
			) : (
				<Typography colorOn={backgroundColor}>Loading...</Typography>
			)}
		</StyledBox>
	);
};

export default LazyImage;
